// React Imports
import React, { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

// MUI Imports
import Box from "@mui/material/Box";
import { useTheme, useMediaQuery } from "@mui/material";

// Download App
import DownloadAppBox from "src/components/common/DownloadApp";
import { APP_URLS } from "src/hooks/useAppDetection";
import { setIsDownloadBoxVisible } from "src/redux/slices/AppSlice";
import Grid from "@mui/material/Grid2";
import { styled } from "@mui/material/styles";
import { Typography } from "@mui/material";

// Images
import rideShareImage from "../assets/images/auth_slide1.png";
import safeJourneyImage from "../assets/images/auth_slide2.png";
import easyBookingImage from "../assets/images/auth_slide3.png";
import bestRatesImage from "../assets/images/slide5.png";
import quickPickupImage from "../assets/images/auth_slide1.png";

import bgImage from "../assets/images/auth_bg_left.png"; // background image
import WelcomeIcon from "src/@core/components/icons/WelcomeIcon";
import RegisterFlow from "src/pages/auth/RegisterFlow";
import LoginPage from "src/pages/auth/login";

// Styled Components
const LeftWrapper = styled(Box)(({ theme }) => ({
  height: "100vh",
  display: "flex",
  position: "relative",
  backgroundImage: `url(${bgImage})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
  backgroundRepeat: "no-repeat",
  overflow: "hidden",
}));

const ContentContainer = styled(Box)({
  position: "relative",
  width: "100%",
  height: "100%",
  display: "flex",
  alignItems: "start",
  justifyContent: "center",
  zIndex: 2,
});

const SlideContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "450px",
  height: "auto",
  background: "transparent",
  borderRadius: "20px",
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  padding: "0px 20px",
}));

const SemiCircleContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  height: "500px",
  backgroundColor: "#f3e8ff",
  borderBottomLeftRadius: "200px",
  borderBottomRightRadius: "200px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  overflow: "hidden",
}));

const SlideImage = styled("img")({
  width: "70%",
  height: "70%",
  objectFit: "contain",
  transition: "all 0.3s ease-in-out",
});

const TitleText = styled(Typography)({
  fontSize: "26px",
  fontWeight: "700",
  color: "#fff",
  textAlign: "center",
  marginTop: "40px",
  textTransform: "lowercase",
});

const SubtitleText = styled(Typography)({
  fontSize: "14px",
  color: "#d1cce8",
  textAlign: "center",
  marginTop: "10px",
  marginBottom: "40px",
});

const DotsContainer = styled(Box)({
  display: "flex",
  gap: "8px",
  alignItems: "center",
  justifyContent: "center",
});

const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== "active",
})(({ active }) => ({
  width: active ? "30px" : "8px",
  height: "8px",
  borderRadius: "4px",
  background: active ? "#00f0ff" : "rgba(255,255,255,0.3)",
  transition: "all 0.3s ease",
  cursor: "pointer",
}));

const RightWrapper = styled(Box)(({ theme }) => ({
  height: "100vh",
  display: "flex",
  margin: "auto",
  [theme.breakpoints.up("md")]: {
    maxWidth: 550,
    width: "70%",
  },
}));

const RightContainer = styled(Box)(({ theme }) => ({
  flex: "1",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

// Slide Content
const slides = [
  {
    title: "beɘmz",
    subtitle: "the out & about chat app",
    image: <WelcomeIcon />,
  },
  {
    title: "discover & chat",
    subtitle: "connect with trusted people",
    image: safeJourneyImage,
  },
  {
    title: "ride share",
    subtitle: "travel with trust",
    image: easyBookingImage,
  },
  {
    title: "events",
    subtitle: "let others know where & what?",
    image: bestRatesImage,
  },
  {
    title: "posts",
    subtitle: "share with friends, family and your community",
    image: quickPickupImage,
  },
];

const AuthLayout = ({ children }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down("sm"));
  const [currentSlide, setCurrentSlide] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);

  const isDownloadBoxVisible = useSelector((state) => state.app.isDownloadBoxVisible);
  const isAppInstalled = useSelector((state) => state.app.isAppInstalled);

  useEffect(() => {
    const interval = setInterval(() => {
      setImageLoaded(false);
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenApp = () => {
    window.location.href = APP_URLS.deepLink;
  };

  const handleDownloadApp = () => {
    const isAndroid = /Android/i.test(navigator.userAgent);
    window.location.href = isAndroid ? APP_URLS.playStore : APP_URLS.appStore;
  };

  const handleHideBox = () => {
    dispatch(setIsDownloadBoxVisible(false));
  };

  const handleDotClick = (index) => {
    setImageLoaded(false);
    setCurrentSlide(index);
  };

  // Render different components based on route
  const renderAuthPage = () => {
    if (location.pathname === '/login') {
      return <LoginPage />;
    } else if (location.pathname === '/register') {
      return <RegisterFlow />;
    }
    // Default: render children for other auth routes (forgot password, reset password, etc.)
    return children;
  };

  return (
    <>
      {isDownloadBoxVisible && isAppInstalled !== null &&
        location.pathname !== '/business/get-started' &&
        location.pathname !== '/business/login' && (
        <Box p="8px 8px 0px 8px" bgcolor="#6e3fc6" display={{ sm: "none", xs: "block" }}>
          <DownloadAppBox
            isDownloadBoxVisible={isDownloadBoxVisible}
            isMobile={isMobileViewport}
            isAppInstalled={isAppInstalled}
            handleOpenApp={handleOpenApp}
            handleDownloadApp={handleDownloadApp}
            handleHideBox={handleHideBox}
          />
        </Box>
      )}
      {renderAuthPage()}
    </>
  );
};

export default AuthLayout;
